import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Divider, Tag, Typography } from 'antd';
import { AnchorLinkItemProps } from 'antd/es/anchor/Anchor';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { Card } from '@hdi-seguros/web-components.card';
import {
  Anchor,
  ColSize,
  ErrorResults,
  RowSize,
  SectionsSkeleton,
} from '../../../../../shared/components';
import {
  Column,
  _renderColumns,
  _renderSection,
} from '../../utils/renderSections';

import ProducersService from '../../../../../shared/services/producers';

import {
  downloadBase64Files,
  formatDate,
  formattedPrice
} from '../../../../../shared/utils';

import { States, StatesColor, StatesValues } from './enums/index.enum';

import { COLORS } from '../../../../../config/themes/colors';
import { ReportFeePaidModal } from '../ReportFeePaidModal';
import './index.css';
import { DECISION_TYPE, FILE_TYPES, Quote } from '../../../../../shared/interfaces';
import DocumentsService from '../../../../../shared/services/documents';
import { PayedFeeDocumentRequest } from '../../../../../shared/interfaces/services/get-payed-fee-document';
import dayjs from 'dayjs';
import { CurrencySymbol } from '../../../../../shared/interfaces/enums/currency-symbols-enum';

export const QuotesTab: FC<any> = ({
  insurancePolicyDetail,
  id,
  endorsement,
  insuredsByPolicy,
  onUpdateEndorsement,
  totalPolicyBalance
}) => {
  const [unpaidQuotes, setUnpaidQuotes] = useState<any>();
  const [paymentMethods, setPaymentMethods] = useState<any>();
  const [totalPolicyBalanceData, setTotalPolicyBalanceData] = useState<any>();
  const [isLoadingTotalBalance, setIsLoadingTotalBalance] = useState<boolean>(true);
  const [endorsement_feed, setEndorsement_feed] = useState<any>();
  const hasProcessedBalanceData = useRef<boolean>(false);
  const [showError, setShowError] = useState(false);
  const [ showReportfeePaidModal, setShowReportfeePaidModal ] = useState<boolean>(false);
  const [quoteToProcess, setQuoteToProcess] = useState<Quote | null>(null);


  const MOCK_SECTIONS: Array<AnchorLinkItemProps> = [
    {
      key: 'metodo-de-pago',
      href: '#metodo-de-pago',
      title: 'Método de Pago',
    },
    {
      key: 'cuotas-del-endoso',
      href: '#cuotas-del-endoso',
      title: `Cuotas del endoso ${endorsement?.nro || "-"}`
    },
    {
      key: 'cuotas-impagas-de-la-poliza',
      href: '#cuotas-impagas-de-la-poliza',
      title: 'Cuotas impagas de la póliza',
    },
    {
      key: 'saldo-total',
      href: '#saldo-total',
      title: 'Saldo Total',
    },
  ];

  const onCloseModal = useCallback(
    () => {
      setShowReportfeePaidModal(false);
    }, 
    []
  );

  const onCloseModalAndUpdate = useCallback(
    () => {
      setShowReportfeePaidModal(false);
      getDetail();

      if (onUpdateEndorsement) {
        onUpdateEndorsement();
      }
    }, 
    [onUpdateEndorsement]
  );


  useEffect(() => {
    if (insurancePolicyDetail?.rama && id) {
      getDetail();
    }
    /* eslint-disable-next-line */
  }, [insurancePolicyDetail?.rama, id]);

  useEffect(() => {
    if (endorsement) getPaymentMethods();    
    /* eslint-disable-next-line */
  }, [endorsement]);

  useEffect(() => {
    if (endorsement) setDetailEndorsement_feed();
    /* eslint-disable-next-line */
  }, [endorsement]);

  useEffect(() => {
    if (hasProcessedBalanceData.current) {
      setIsLoadingTotalBalance(false);
      return;
    }

    if (totalPolicyBalance && insurancePolicyDetail?.moneda) {
      setIsLoadingTotalBalance(true);
      getTotalPolicyBalance();
    } else if (!totalPolicyBalance) {
      setIsLoadingTotalBalance(true);
    } else {
      setIsLoadingTotalBalance(false);
    }
    /* eslint-disable-next-line */
  }, [totalPolicyBalance, insurancePolicyDetail?.moneda]);

  const getDetail = async () => {
    if (!insurancePolicyDetail?.rama || !id) {
      return;
    }

    try {
      const branch = insurancePolicyDetail.rama;
      const response = await ProducersService.getQuotes(branch, id);
      
      setUnpaidQuotes(response);
      setShowError(false);
    } catch (error) {
      setShowError(true);
    }
  };

  const getPaymentMethods = () => {

    const creditCardNumber = insurancePolicyDetail?.formaDePago?.numeroTarjetaDeCredito;
    const cbuNumber = endorsement.NroCBUSin && endorsement.NroCBUSin !== '' 
      ? endorsement.NroCBUSin 
      : endorsement.nroCbu; 
    const data = [
      {
        label: 'Forma de pago',
        value:
          endorsement.formaDePago === '' || !endorsement.formaDePago
            ? '-'
            : endorsement.formaDePago,
      },
      {
        label: 'Banco Emisor',
        value:
          endorsement.banco === '' || !endorsement.banco
            ? '-'
            : endorsement.banco,
      },
    ];

    if (cbuNumber && cbuNumber !== '' && cbuNumber !== '0000000000000000000000000') {
      data.push({
        label: 'Número de cuenta',
        value: cbuNumber,
      });
    }

    if (creditCardNumber && creditCardNumber !== '' && creditCardNumber !== '0000000000000000000000000') {
      data.push({
        label: 'Número de tarjeta de crédito',
        value: creditCardNumber,
      });
    }

    setPaymentMethods(data);
  };

  const getTotalPolicyBalance = () => {
    if (!totalPolicyBalance || !insurancePolicyDetail?.moneda) {
      setIsLoadingTotalBalance(false);
      return;
    }

    const data = [
      {
        label: 'Premio Total',
        value: formatValue(totalPolicyBalance.premioTotal, insurancePolicyDetail.moneda),
      },
      {
        label: 'Pagos',
        value: formatValue(totalPolicyBalance.pagos, insurancePolicyDetail.moneda),
      },
      {
        label: 'Saldo Total',
        value: formatValue(totalPolicyBalance.saldoTotal, insurancePolicyDetail.moneda),
      },
    ];

    setTotalPolicyBalanceData(data);
    hasProcessedBalanceData.current = true;
    setIsLoadingTotalBalance(false);
  };

  const downloadQuote = () => {
    // TODO Agregar lógica para descargar la cuota    
  };

  const formatValue = (value: number | string | null | undefined, symbol: String) => {
    if (value == null) return '-';
    if (value === '') return '-';
    return `${symbol == '' ? CurrencySymbol.PESOS_ARGENTINOS : symbol} ${formattedPrice(String(value))}`;
  };


  const openReportFeePaidModal = () => {
    setShowReportfeePaidModal(true);
  };

  const setDetailEndorsement_feed = () => {
    const isPagada = endorsement.cuotas.filter(
      (payment: { isPagada: string }) => payment.isPagada.toUpperCase() === DECISION_TYPE.YES
    );

    const paymentAmount = isPagada.reduce(
      (total: number, element: { importePago: string }) =>
        total + parseFloat(element.importePago),
      0
    );

    const balance = parseFloat(endorsement.premio) - parseFloat(paymentAmount);

    const data = [
      {
        label: 'Premio emitido',
        value: `${endorsement.moneda} ${formattedPrice(endorsement.premio)}`,
      },
      {
        label: 'Importe Pago',
        value: `${endorsement.moneda} ${
          paymentAmount === 0 ? paymentAmount : formattedPrice(paymentAmount)
        }`,
      },
      {
        label: 'Saldo',
        value: `${endorsement.moneda} ${formattedPrice(balance.toString())}`,
      },
    ];

    setEndorsement_feed(data);
  };

  const setDetailQuotes = () => {
    const columnSize = {
      xs: { span: 12 },
      sm: { span: 7 },
      md: { span: 4 },
    };

    const modifiedQuotes = endorsement.cuotas.map((item: Quote) => {
      const data = [
        {
          label: 'Cuota',
          value: item.nroCuota === '' || !item.nroCuota ? '-' : item.nroCuota,
          columnSize,
          isLikeTitle: true,
          valueInBold: true,
        },
        {
          label: 'Vencimiento',
          value:
            item.fecVto === '' || !item.fecVto ? '-' : formatDate(item.fecVto),
          columnSize,
        },
        {
          label: 'Importe',
          value:
            item.importeCuota === '' || !item.importeCuota
              ? '-'
              : `${item.moneda} ${formattedPrice(item.importeCuota)}`,
          columnSize,
        },
        {
          label: 'Estado',
          value: (
            <Tag
              color={
                StatesColor[
                  (item?.isPagada === DECISION_TYPE.YES && !item?.fecPago) ?
                   StatesValues.STATE_2 as keyof typeof StatesColor :
                  `STATE_${item.estadoDeCuota}` as keyof typeof StatesColor
                ]
              }
            >
              {States[
                (item?.isPagada === DECISION_TYPE.YES && !item?.fecPago) ?
                StatesValues.STATE_2 as keyof typeof StatesColor :
                `STATE_${item.estadoDeCuota}` as keyof typeof States
                ]}
            </Tag>
          ),
          columnSize,
        },
        {
          label: 'Origen Pago	',
          value: item?.origenDelPago.length? item.origenDelPago : '-',
          columnSize,
        },
        {
          label: 'Fecha de Pago',
          value: !item?.fecPago ? '-': dayjs(item.fecPago).format('DD/MM/YYYY'),
          columnSize,
        },
        {
          label: 'Comprobante de Pago',
          value:
            item.permiteRecibo.toUpperCase() !== DECISION_TYPE.YES || !item.permiteRecibo ? (
              '-'
            ) : (
              <ActionButton
                label='Descargar'
                onClick={() => {
                  callServicePayedFeeDocument(item);
                }}
                icon={<DownloadOutlined />}
              />
            ),
          columnSize,
        },
        {
          label: 'Informar Pago',
          value:
            !item.isPagada ? (
              '-'
            ) : (
              <ActionButton
              className='btn-accent-barbuss'
                label='Subir comprobante'
                onClick={() => {
                  openReportFeePaidModal();
                  setQuoteToProcess(item);
                }}
                disabled={!['0', '1'].includes(`${item.estadoDeCuota}`)}
                icon={<UploadOutlined />}
              />
            ),
          columnSize,
        },
      ];

      return data;
    });

    return modifiedQuotes;
  };

  const _renderQuoteDetail = () => {
    const columns = setDetailQuotes();

    return (
      <>
        <Typography.Text style={{ fontWeight: '600', margin: '1rem 0 0 1rem' }}>
          Detalle de cuotas
        </Typography.Text>
        <RowSize rowProps={{gutter:[10 , 10]}} className='row-quotes-cards'>
          {columns.map((item: Column[], index: number) => (
            <Card
              key={`card-${item[index]?.label}-index`}
              style={{
                background: COLORS.neutrals[15],
                width: '100%',
              }}
            >
              <RowSize rowProps={{gutter:[0 , 15]}}>{_renderColumns(item)}</RowSize>
            </Card>
          ))}
        </RowSize>
      </>
    );
  };

  const _renderUnpaidQuote = () => {
    return (
      <>
        <RowSize rowProps={{gutter:[0 , 0]}}>
          <Typography.Title level={ 4 } style={{ margin: 0, marginBottom: 10, marginTop: 20, color: COLORS.principal[100]}}
          >
            Cuotas Impagas de la póliza
          </Typography.Title>
        </RowSize>

        <RowSize rowProps={{gutter:[10 , 10]}}>
          {`No hay cuotas impagas para la póliza ${id}`}
        </RowSize>
      </>
    );
  };

  const _renderUnpaidQuiteDetail = (hasDivider = true) => (
    <>
      <Typography.Title level={ 4 } style={{ margin: 0, marginBottom: 10, marginTop: 20, color: COLORS.principal[100] }}
      >
        Cuotas impagas de la póliza
      </Typography.Title>
      <RowSize rowProps={{gutter:[10 , 10]}} className='row-quotes-not-paid-cards' >
        {unpaidQuotes.map((item: Column[], index: number) => (
          <Card
            key={`card-${item[index]?.label}-index`}
            style={{
              background: COLORS.neutrals[15],
              width: '100%',
            }}
          >
            <RowSize rowProps={{gutter:[0 , 15]}}>{_renderColumns(item)}</RowSize>
          </Card>
        ))}
      </RowSize>
      {
            hasDivider
                ? <Divider style={{ minWidth: '90%', width: '98%' }} /> 
                : <></>
        }
    </>
  );

  function getValueByLabel(section: any[], label: string): string | undefined {
    return section.find(item => item.label === label)?.value;
  }

const callServicePayedFeeDocument = async (quote : Quote) => {
  const data: PayedFeeDocumentRequest = {
    fechaDeRegistracion: quote.fecPago,
    asegurado: insurancePolicyDetail.nombreAsegurado,
    domicilio: getValueByLabel(insuredsByPolicy.address, "Domicilio") ?? '-',
    codigoPostal: Number(getValueByLabel(insuredsByPolicy.address, "Código Postal")) ?? '-',
    localidad: getValueByLabel(insuredsByPolicy.address, "Localidad") ?? '',
    rama: insurancePolicyDetail.descripcionRama,
    poliza: insurancePolicyDetail.nroPoliza,
    suplemento: Number(quote.suplemento),
    cuota: quote.nroCuota,
    importe: quote.importeCuota,
    moneda: quote.moneda
  };

  try {
    const response = await DocumentsService.getPayedFeeDocument(data);
    const base64 = response.data.base64;
    const filename = "ReciboCuota_" + quote.nroCuota + ".pdf";

    downloadBase64Files(base64, filename, FILE_TYPES.PDF);
  } catch (error) {
    
  }
};


  return (
    <div className='quotes-tab-container'>
      {showError ? (
        <ErrorResults title='Hubo un error intentando cargar las cuotas de la póliza' />
      ) : unpaidQuotes && paymentMethods ? (
        <RowSize rowProps={{gutter:[0 , 0]}}>
          
          <ColSize size={85} colProps={{ xs:{span: 21}, sm:{span: 21}}}>
            {_renderSection(
              'Método de Pago',
              'metodos-de-pago',
              paymentMethods
            )}
            {endorsement_feed?.length === 0
              ? _renderSection(
                  `Cuotas del endoso ${endorsement.nro}`,
                  'cuotas-del-endoso',
                  endorsement_feed
                )
              : _renderSection(
                  `Cuotas del endoso ${endorsement.nro}`,
                  'cuotas-del-endoso',
                  endorsement_feed,
                  _renderQuoteDetail()
                )}
            {unpaidQuotes.length > 0
              ? _renderUnpaidQuiteDetail()
              : _renderUnpaidQuote()}

            {totalPolicyBalanceData && !isLoadingTotalBalance && _renderSection(
              'Saldo Total',
              'saldo-total',
              totalPolicyBalanceData
            )}
          </ColSize>

          <ColSize size={9} colProps={{id:'quotes-tab-container-anchor', xs:{span: 3}, sm:{span: 3}}}>
            <Anchor
              items={MOCK_SECTIONS}
              containerId='quotes-tab-container-anchor'
            />
          </ColSize>
        </RowSize>
      ) : (
        <SectionsSkeleton />
      )}

{
        showReportfeePaidModal && quoteToProcess != null && (
          <ReportFeePaidModal
            title={`Subir comprobante de cuota pagada`} 
            open={ showReportfeePaidModal } 
            quote={quoteToProcess}
            endorsement={endorsement}
            insurancePolicyDetail={insurancePolicyDetail}
            onClose={onCloseModal}
            onUpdate={onCloseModalAndUpdate}
          />
        )
      }
    </div>
  );
};
